var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { PatientNotificationType } from "healthex-common/study.dtos";
import { FormModel } from "../FormModel";
import { computed, observable, action } from "mobx";
import { NamedRoutes } from "../NamedRoutes";
import { isAlphaNumeric } from "healthex-common/utils";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { StudyTranslationLanguage } from "healthex-common/study-translation-language.dtos";
export class AddPatientDataOriginatorDialogModel {
    constructor(opts) {
        this.currentTab = "ADD_PATIENT_MRN";
        this.mrns = [];
        this.isSubmitting = false;
        this.validMRNs = new Set();
        this.invalidMRNs = new Set();
        this.searchingMRNs = new Set();
        this.mrnValidationErrors = new Map();
        this.isPreviewDialogOpen = false;
        this.previewData = null;
        this.isLoadingPreview = false;
        this.addPatientMRNFormModel = new FormModel({
            patientMRNs: "",
            patientNotificationType: PatientNotificationType.ENROLLMENT_REQUESTED,
        }, ["patientMRNs", "patientNotificationType"]);
        this.sharePatientLinkFormModel = new FormModel({}, []);
        this.navigateToProjects = () => {
            if (!this.projectId) {
                console.error("No projectId found in route parameters");
                return;
            }
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER_PATIENT_MANAGEMENT, {
                studyId: this.projectId,
            });
        };
        this.closePreviewDialog = () => {
            this.isPreviewDialogOpen = false;
            this.previewData = null;
            this.isLoadingPreview = false;
        };
        this.clearFormData = () => {
            this.addPatientMRNFormModel.input.patientMRNs = "";
            this.addPatientMRNFormModel.input.patientNotificationType = PatientNotificationType.ENROLLMENT_REQUESTED;
            this.mrns = [];
            this.validMRNs.clear();
            this.invalidMRNs.clear();
            this.searchingMRNs.clear();
            this.mrnValidationErrors.clear();
        };
        this.addPatients = async () => {
            if (!this.projectId || !this.organizationId) {
                return false;
            }
            if (!this.canSubmit) {
                if (this.mrns.length === 0) {
                    console.error("No MRNs to add");
                }
                else if (this.isSearching) {
                    console.log("MRNs are still being validated");
                }
                else if (!this.hasValidMRNs) {
                    console.log("No valid MRNs found. Please check your MRN entries.");
                }
                return false;
            }
            this.isSubmitting = true;
            try {
                const mrnDtos = Array.from(this.validMRNs).map((mrn) => ({
                    mrn,
                    patientNotificationType: this.addPatientMRNFormModel.input.patientNotificationType,
                }));
                if (mrnDtos.length === 0) {
                    console.error("No MRNs to add");
                    return false;
                }
                const input = {
                    mrns: mrnDtos,
                };
                const result = await this.patientService.addPatients(this.projectId, input);
                let bannerMessage = `${result.successCount} patient${result.successCount === 1 ? "" : "s"} added from MRNs`;
                const details = [];
                if (result.errorCount > 0) {
                    details.push(`${result.errorCount} had errors`);
                }
                if (result.duplicatesCount > 0) {
                    details.push(`${result.duplicatesCount} duplicate${result.duplicatesCount === 1 ? " was" : "s were"} skipped`);
                }
                if (this.hasInvalidMRNs) {
                    const invalidCount = this.invalidMRNs.size;
                    details.push(`${invalidCount} invalid MRN${invalidCount === 1 ? "" : "s"} skipped`);
                }
                if (details.length > 0) {
                    bannerMessage += `, ${details.join(", ")}`;
                }
                this.bannerUIManager.displayBanner({ text: bannerMessage, type: "SUCCESS" });
                this.clearFormData();
                const success = result.successCount > 0;
                return success;
            }
            catch (error) {
                console.error("Error adding patients by MRN:", error);
                this.bannerUIManager.displayBanner({
                    text: "Failed to add patients. Please try again.",
                    type: "ERROR",
                });
                return false;
            }
            finally {
                this.isSubmitting = false;
            }
        };
        this.studiesUIManager = opts.container.studiesUIManager;
        this.routingUIManager = opts.container.routingUIManager;
        this.authManager = opts.container.authManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.mrnSearchService = opts.container.mrnSearchService;
        this.patientService = opts.container.patientService;
        this.onClose = opts.onClose;
    }
    get organizationId() {
        return this.authManager.orgId;
    }
    get projectId() {
        var _a;
        return (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.projectId;
    }
    get study() {
        return this.studiesUIManager.studyToManage;
    }
    updateMRNs(mrns) {
        this.mrns = mrns;
        const currentMRNs = new Set(mrns.map((mrn) => mrn.trim()).filter((mrn) => mrn));
        const previousMRNs = new Set([...this.validMRNs, ...this.invalidMRNs, ...this.searchingMRNs]);
        const removedMRNs = [...previousMRNs].filter((mrn) => !currentMRNs.has(mrn));
        removedMRNs.forEach((mrn) => {
            this.validMRNs.delete(mrn);
            this.invalidMRNs.delete(mrn);
            this.searchingMRNs.delete(mrn);
            this.mrnValidationErrors.delete(mrn);
        });
        const newMRNs = [...currentMRNs].filter((mrn) => !previousMRNs.has(mrn));
        newMRNs.forEach((mrn) => this.validateAndSearchMRN(mrn));
    }
    get formModel() {
        return this.currentTab === "ADD_PATIENT_MRN" ? this.addPatientMRNFormModel : this.sharePatientLinkFormModel;
    }
    get tabsToShow() {
        return [
            { value: "ADD_PATIENT_MRN", label: "Add specific patients" },
            { value: "SHARE_PATIENT_LINK", label: "Share patient experience link" },
        ];
    }
    setCurrentTab(tab) {
        this.currentTab = tab;
    }
    get areNotificationsSuppressed() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.suppressNotifications;
    }
    get getContactMethodText() {
        return this.areNotificationsSuppressed
            ? "Select the notification type to send"
            : "Select the type of notification that will be been sent";
    }
    get hasValidMRNs() {
        return this.validMRNs.size > 0;
    }
    get hasInvalidMRNs() {
        return this.invalidMRNs.size > 0;
    }
    get isSearching() {
        return this.searchingMRNs.size > 0;
    }
    get canSubmit() {
        return this.hasValidMRNs && !this.isSearching && !this.isSubmitting;
    }
    get validationErrorMessages() {
        if (this.hasInvalidMRNs) {
            const errorDetails = [];
            Array.from(this.invalidMRNs).forEach((mrn) => {
                const errors = this.mrnValidationErrors.get(mrn);
                if (errors && errors.length > 0) {
                    errorDetails.push(`${mrn}: ${errors.join(", ")}`);
                }
                else {
                    errorDetails.push(`${mrn}: Invalid`);
                }
            });
            return errorDetails;
        }
        return [];
    }
    get validationErrorText() {
        return this.validationErrorMessages.join("\n");
    }
    async validateAndSearchMRN(mrn) {
        if (!isAlphaNumeric(mrn)) {
            this.invalidMRNs.add(mrn);
            this.mrnValidationErrors.set(mrn, ["MRN must contain only letters and numbers"]);
            return;
        }
        this.searchingMRNs.add(mrn);
        this.mrnValidationErrors.delete(mrn);
        try {
            if (!this.organizationId) {
                console.error("Missing organizationId for MRN search");
                this.invalidMRNs.add(mrn);
                this.mrnValidationErrors.set(mrn, ["Organization ID is missing"]);
                return;
            }
            if (!this.projectId) {
                console.error("Missing projectId for MRN search");
                this.invalidMRNs.add(mrn);
                this.mrnValidationErrors.set(mrn, ["Project ID is missing"]);
                return;
            }
            const searchInput = {
                mrn,
                projectId: this.projectId,
            };
            const result = await this.mrnSearchService.searchByMRNAndGetPatientDetailsWithoutConsents(this.organizationId, searchInput);
            if (result) {
                if (result.errors && result.errors.length > 0) {
                    const errorMessages = result.errors.map((err) => err.error);
                    this.mrnValidationErrors.set(mrn, errorMessages);
                    this.invalidMRNs.add(mrn);
                }
                else if (result.duplicates && result.duplicates.length > 0) {
                    const duplicateMessages = result.duplicates.map((dup) => `Patient is already part of this project`);
                    this.mrnValidationErrors.set(mrn, duplicateMessages);
                    this.invalidMRNs.add(mrn);
                }
                else if (result.successCount > 0) {
                    this.validMRNs.add(mrn);
                }
                else {
                    this.invalidMRNs.add(mrn);
                    this.mrnValidationErrors.set(mrn, ["Patient validation failed"]);
                }
            }
            else {
                this.invalidMRNs.add(mrn);
                this.mrnValidationErrors.set(mrn, ["Patient not found with this MRN"]);
            }
        }
        catch (error) {
            console.error(`Failed to search for MRN ${mrn}:`, error);
            this.invalidMRNs.add(mrn);
            this.mrnValidationErrors.set(mrn, ["Failed to search for patient"]);
        }
        finally {
            this.searchingMRNs.delete(mrn);
        }
    }
    async previewNotifications() {
        if (!this.projectId || !this.organizationId) {
            console.error("Missing projectId or organizationId");
            return;
        }
        try {
            this.isLoadingPreview = true;
            this.isPreviewDialogOpen = true;
            this.previewData = null;
            const previewRequest = {
                projectId: this.projectId,
                patientNotificationType: this.validatedNotificationType,
                language: StudyTranslationLanguage.en,
            };
            const preview = await this.patientService.previewNotification(this.projectId, this.organizationId, previewRequest);
            this.previewData = preview;
        }
        catch (error) {
            console.error("Failed to load notification preview:", error);
            this.bannerUIManager.displayBanner({ text: "Failed to load notification preview. Please try again." });
            this.closePreviewDialog();
        }
        finally {
            this.isLoadingPreview = false;
        }
    }
    resetTabs() {
        this.currentTab = "ADD_PATIENT_MRN";
    }
    resetForm() {
        this.clearFormData();
        this.resetTabs();
    }
    get studyToManage() {
        return this.studiesUIManager.studyToManage;
    }
    get isPdda() {
        if (this.studyToManage === NOT_LOADED || this.studyToManage === null)
            return false;
        return this.studyToManage.pdda;
    }
    get availableNotificationTypes() {
        const options = [];
        options.push(PatientNotificationType.ENROLLMENT_REQUESTED);
        return options;
    }
    get shouldShowNotificationTypeSelection() {
        return this.availableNotificationTypes.length > 1;
    }
    get validatedNotificationType() {
        const current = this.addPatientMRNFormModel.input.patientNotificationType;
        const available = this.availableNotificationTypes;
        if (available.includes(current)) {
            return current;
        }
        return available[0] || PatientNotificationType.ENROLLMENT_REQUESTED;
    }
}
__decorate([
    observable,
    __metadata("design:type", String)
], AddPatientDataOriginatorDialogModel.prototype, "currentTab", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AddPatientDataOriginatorDialogModel.prototype, "mrns", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientDataOriginatorDialogModel.prototype, "isSubmitting", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientDataOriginatorDialogModel.prototype, "validMRNs", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientDataOriginatorDialogModel.prototype, "invalidMRNs", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientDataOriginatorDialogModel.prototype, "searchingMRNs", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientDataOriginatorDialogModel.prototype, "mrnValidationErrors", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], AddPatientDataOriginatorDialogModel.prototype, "isPreviewDialogOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddPatientDataOriginatorDialogModel.prototype, "previewData", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], AddPatientDataOriginatorDialogModel.prototype, "isLoadingPreview", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "organizationId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "projectId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "study", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "formModel", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "tabsToShow", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], AddPatientDataOriginatorDialogModel.prototype, "setCurrentTab", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "areNotificationsSuppressed", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "getContactMethodText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "hasValidMRNs", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "hasInvalidMRNs", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "isSearching", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "canSubmit", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "validationErrorMessages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "validationErrorText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "studyToManage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "isPdda", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "availableNotificationTypes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "shouldShowNotificationTypeSelection", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], AddPatientDataOriginatorDialogModel.prototype, "validatedNotificationType", null);
